/* Outer grid matches the legend in OrderSheet.module.css so the
   "US Warehouse" / "International Warehouse" headers sit above their
   data columns exactly. */
.row {
  display: grid;
  grid-template-columns: minmax(200px, 260px) minmax(120px, 150px) 1fr 1fr;
  grid-auto-rows: max-content;
  column-gap: 14px;
  row-gap: 4px;
  padding: 12px 14px 14px;
  margin-bottom: 10px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  align-items: start;
  align-content: start;
  transition: background 120ms ease, border-color 120ms ease;
  container-type: inline-size;
  container-name: orderRow;
}

.rowActive {
  background: #fffaf2;
  border-color: rgba(243, 154, 46, 0.45);
}

/* Head spans col 1 across all variant rows. Column width is unchanged;
   the image sits below the name and uses the full column width so it
   reads at a real product-glance size without pushing the data columns. */
.head {
  grid-column: 1;
  grid-row: 1 / span 99;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: start;
  padding-top: 2px;
}

.thumbWrap {
  display: block;
  width: 100%;
  margin-top: 4px;
}

.thumb {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  background: var(--color-bone);
  border-radius: 8px;
}

.name {
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--color-ink);
  display: block;
  text-decoration: none;
  line-height: 1.2;
  letter-spacing: -0.005em;
}

.name:hover { color: var(--color-ember-dark); }

.sub {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 2px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.itemRollup {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: var(--color-ember);
  color: #111;
  border-radius: 4px;
  font-size: 0.78rem;
  font-weight: 700;
}

/* Variant row — a compact 2-column slice that sits in cols 2/3 of the
   outer grid via display: contents on the wrapper. */
.variantRow {
  display: contents;
}

.variantMeta {
  grid-column: 2 / -1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 4px;
  font-size: 0.82rem;
  color: var(--color-ink);
  border-bottom: 1px dashed rgba(0,0,0,0.06);
  margin-top: 4px;
}

/* First variant of each card has no top margin (sits flush). */
.variantRow:first-of-type .variantMeta { margin-top: 0; }

.totalCell {
  grid-column: 2;
  background: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  text-align: right;
  min-width: 0;
  transition: border-color 120ms ease, background 120ms ease;
}

.totalCellActive {
  border-color: var(--color-ember);
  background: #fffaf2;
  box-shadow: inset 0 0 0 1px rgba(243, 154, 46, 0.25);
}

.totalQty {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-ink-soft);
}

.totalCellActive .totalQty { color: var(--color-ember-dark); }

.totalAmount {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-ink);
}

.totalCellEmpty {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
}

.totalIdle { color: rgba(0, 0, 0, 0.22); font-weight: 500; }

.variantMetaActive { color: var(--color-ember-dark); font-weight: 600; }

.swatch {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex: 0 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.variantName { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.size { color: var(--color-ink-soft); }

.cell {
  background: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: border-color 120ms ease, background 120ms ease;
}

.cellActive {
  border-color: var(--color-ember);
  background: #fffaf2;
}

.cellLabel {
  display: none; /* desktop hides — legend covers it */
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-ink-soft);
  text-transform: uppercase;
}

.cellLine {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.price {
  font-size: 0.95rem;
  font-weight: 700;
  white-space: nowrap;
}

.stock {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.ok      { color: #2e9d57; }
.low     { color: var(--color-ember-dark); }
.out     { color: #b53b3b; }
.unknown { color: var(--color-ink-soft); }

/* Container query — when the row itself is narrow (mid-range
   landscape phones, narrow tablet sidebars, split desktop windows)
   it collapses to a single column. Decoupled from viewport. */
@container orderRow (max-width: 760px) {
  .row {
    grid-template-columns: 1fr;
    row-gap: 4px;
    padding: 12px;
    margin-bottom: 10px;
  }
  .head {
    grid-row: auto;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0,0,0,0.06);
    margin-bottom: 4px;
  }
  .head .name { font-size: 1rem; }
  .thumbWrap { width: 64px; flex: 0 0 64px; order: -1; margin: 0; }
  .thumb { width: 64px; height: 64px; aspect-ratio: 1 / 1; }
  .sub { margin-top: 2px; }
  .variantMeta {
    grid-column: 1;
    padding: 8px 0 4px;
    border-bottom: 0;
    justify-content: space-between;
  }
  .totalCell {
    grid-column: 1;
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    text-align: left;
    padding: 6px 10px;
  }
  .totalQty { font-size: 0.7rem; }
  .cellLabel { display: inline-flex; gap: 4px; }
  .cell { padding: 8px 10px; }
}
